import { useRef, useEffect, useState } from "react";
import { API_BASE } from "../config/api";
import { useReveal } from "../hooks/useReveal";

interface ContactInfo {
  icon: string;
  title: string;
  value: string;
  description: string;
  link: string;
}

interface ContactSettings {
  email: string;
  phone: string;
  address: {
    city: string;
    country: string;
    full: string;
  };
  responseTime: string;
}

interface SocialSettings {
  github?: string;
  linkedin?: string;
  twitter?: string;
  instagram?: string;
  youtube?: string;
}

interface WhyChooseUs {
  title: string;
  benefits: string[];
}

// Social link config
const socialConfig = [
  { key: "github", name: "GitHub" },
  { key: "linkedin", name: "LinkedIn" },
  { key: "twitter", name: "Twitter" },
  { key: "instagram", name: "Instagram" },
  { key: "youtube", name: "YouTube" },
];

// Default fallback content
const defaultContactInfo: ContactInfo[] = [
  {
    icon: "▣",
    title: "Email Us",
    value: "hello@ionicerrrrscode.com",
    description: "Send us an email anytime",
    link: "mailto:hello@ionicerrrrscode.com",
  },
  {
    icon: "◑",
    title: "Call Us",
    value: "+92 360 923617",
    description: "Mon-Fri from 9am to 6pm",
    link: "tel:+92360923617",
  },
  {
    icon: "◦",
    title: "Visit Us",
    value: "Lahore, Pakistan",
    description: "Johar Town Block J, J2, Lahore, Pakistan",
    link: "#",
  },
  {
    icon: "▤",
    title: "Response Time",
    value: "Within 24 hours",
    description: "Get instant support",
    link: "#",
  },
];

const defaultSocialLinks = [
  { name: "GitHub", link: "https://github.com/ionicerrrrscode" },
  { name: "LinkedIn", link: "https://linkedin.com/company/ionicerrrrscode" },
];

const defaultWhyChoose: WhyChooseUs = {
  title: "Why Choose Us?",
  benefits: [
    "Fast Development Cycles",
    "Premium Quality Code",
    "Secure & Scalable Solutions",
    "Dedicated Support Team",
    "Mobile-First Approach",
    "Modern UI/UX Design",
  ],
};

export default function Contact() {
  const ref = useRef(null);
  const reveal = useReveal<HTMLDivElement>();
  const [contactInfo, setContactInfo] =
    useState<ContactInfo[]>(defaultContactInfo);
  const [socialLinks, setSocialLinks] = useState(defaultSocialLinks);
  const [whyChooseUs, setWhyChooseUs] = useState<WhyChooseUs>(defaultWhyChoose);

  // Fetch contact info from API
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const [contactRes, socialRes, whyRes] = await Promise.all([
          fetch(`${API_BASE}/api/v1/settings/contact`),
          fetch(`${API_BASE}/api/v1/settings/social`),
          fetch(`${API_BASE}/api/v1/content/why-choose-us`),
        ]);

        const contactData = await contactRes.json();
        const socialData = await socialRes.json();
        const whyData = await whyRes.json();

        if (contactData.success && contactData.data) {
          const settings: ContactSettings = contactData.data;
          setContactInfo([
            {
              icon: "▣",
              title: "Email Us",
              value: settings.email,
              description: "Send us an email anytime",
              link: `mailto:${settings.email}`,
            },
            {
              icon: "◑",
              title: "Call Us",
              value: settings.phone,
              description: "Mon-Fri from 9am to 6pm",
              link: `tel:${settings.phone.replace(/\s+/g, "")}`,
            },
            {
              icon: "◦",
              title: "Visit Us",
              value: `${settings.address.city}, ${settings.address.country}`,
              description:
                settings.address.full || "Come say hello at our office",
              link: "#",
            },
            {
              icon: "▤",
              title: "Response Time",
              value: settings.responseTime || "Within 24 hours",
              description: "Get instant support",
              link: "#",
            },
          ]);
        }

        if (socialData.success && socialData.data) {
          const social: SocialSettings = socialData.data;
          const dynamicLinks = socialConfig
            .filter((config) => {
              const url = social[config.key as keyof SocialSettings];
              return url && url.trim() !== "";
            })
            .map((config) => ({
              name: config.name,
              link: social[config.key as keyof SocialSettings] as string,
            }));

          if (dynamicLinks.length > 0) {
            setSocialLinks(dynamicLinks);
          }
        }

        if (whyData.success && whyData.data) {
          const benefits =
            Array.isArray(whyData.data.benefits) &&
            whyData.data.benefits.length > 0
              ? whyData.data.benefits
              : defaultWhyChoose.benefits;
          setWhyChooseUs({
            title: whyData.data.title || defaultWhyChoose.title,
            benefits,
          });
        }
      } catch (error) {
        console.error("Failed to fetch contact info:", error);
        // Keep default content on error
      }
    };
    fetchContactInfo();
  }, []);

  return (
    <section id="contact" ref={ref} className="section-spacing">
      <div className="container-page">
        {/* Section Header */}
        <div className="section-head">
          <span className="eyebrow">Get In Touch</span>
          <h2 className="mt-4" style={{ color: "var(--text)" }}>
            Let's Create Something Amazing
          </h2>
          <p className="mt-4 text-[1.06rem]" style={{ color: "var(--muted)" }}>
            Ready to transform your ideas into reality? Reach out to us through
            any of the channels below.
          </p>
        </div>

        <div
          ref={reveal.ref}
          className="grid lg:grid-cols-2 gap-16"
          style={{
            opacity: reveal.isVisible ? 1 : 0,
            transform: reveal.isVisible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity .7s var(--ease), transform .7s var(--ease)",
          }}
        >
          {/* Contact Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {contactInfo.map((info) => (
              <a
                key={info.title}
                href={info.link}
                className="card-studio no-underline text-center"
                style={{ display: "block" }}
              >
                <div className="icon-chip mx-auto mb-3">{info.icon}</div>
                <h4
                  className="font-display text-base font-semibold mb-1"
                  style={{ color: "var(--text)" }}
                >
                  {info.title}
                </h4>
                <p
                  className="text-sm font-medium mb-1"
                  style={{ color: "var(--text)" }}
                >
                  {info.value}
                </p>
                <p className="text-xs" style={{ color: "var(--muted)" }}>
                  {info.description}
                </p>
              </a>
            ))}
          </div>

          {/* Why Choose Us + Social Links */}
          <div className="space-y-6">
            <div className="card-studio">
              <h3
                className="font-display text-lg font-semibold mb-5"
                style={{ color: "var(--text)" }}
              >
                {whyChooseUs.title}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {whyChooseUs.benefits.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-2">
                    <span
                      style={{
                        color: "var(--accent-green)",
                        fontSize: "0.5rem",
                      }}
                    >
                      ●
                    </span>
                    <span className="text-sm" style={{ color: "var(--muted)" }}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-studio">
              <h3
                className="font-display text-lg font-semibold mb-5"
                style={{ color: "var(--text)" }}
              >
                Follow Us
              </h3>
              <div className="flex flex-wrap gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tag-chip no-underline transition-all duration-300"
                    style={{ color: "var(--muted)" }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.color = "var(--text)";
                      (e.target as HTMLElement).style.borderColor =
                        "var(--border-strong)";
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.color = "var(--muted)";
                      (e.target as HTMLElement).style.borderColor =
                        "var(--border)";
                    }}
                  >
                    {social.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
